import { memo, useMemo } from 'react';
import { Icon, Link, Text, useTheme } from '@Sage/hoi-poi-ui';

import { getMomentFromDateBackend } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import {
    getMarketingCampaignInteractionDisplayUrl,
    getMarketingCampaignInteractionIcon,
    getMarketingCampaignInteractionLabel,
    getMarketingCampaignInteractionTimestamp,
    getMarketingCampaignInteractionType,
    getMarketingCampaignInteractionUrl,
    getMarketingCampaignInteractions,
} from '../../../../utils/marketingCampaignActivity';

import '../../styles.scss';

const getInteractionIconColor = (type, theme) => {
    switch (type) {
        case 'open':
            return theme.colors.semantic.info500;
        case 'click':
            return theme.colors.semantic.positive500;
        case 'unsubscribe':
            return theme.colors.semantic.negative500;
        case 'delivered':
        default:
            return theme.colors.grey[500];
    }
};

const formatInteractionDate = (timestamp) => {
    if (!timestamp) return null;
    const momentDate = getMomentFromDateBackend(timestamp, null, true);
    return getLiteralWithParameters('label_date_with_time', [
        momentDate.format('L'),
        momentDate.format('LT'),
    ]);
};

const DetailMarketingCampaignInteractions = memo(({ activity }) => {
    const theme = useTheme();

    const interactions = useMemo(
        () => getMarketingCampaignInteractions(activity?.Campaign),
        [activity?.Campaign],
    );

    return (
        <div className="fm-activities-marketing-campaign-interactions">
            <div className="fm-activities-marketing-campaign-interactions__header">
                <Text medium>
                    {getLiteral(
                        'label_marketing_campaigns_activity_interactions_recipient_actions_title',
                    )}
                </Text>
            </div>
            <div className="fm-activities-marketing-campaign-interactions__list">
                {interactions.map((interaction, index) => {
                    const type = getMarketingCampaignInteractionType(interaction);
                    const timestamp = getMarketingCampaignInteractionTimestamp(interaction);
                    const url =
                        type === 'click' ? getMarketingCampaignInteractionUrl(interaction) : '';
                    const displayUrl = url ? getMarketingCampaignInteractionDisplayUrl(url) : '';
                    const dateText = formatInteractionDate(timestamp);
                    const iconName = getMarketingCampaignInteractionIcon(type);
                    const iconColor = getInteractionIconColor(type, theme);

                    return (
                        <div
                            key={`${type}-${timestamp}-${index}`}
                            className="fm-activities-marketing-campaign-interactions__item"
                        >
                            <div className="fm-activities-marketing-campaign-interactions__main">
                                <Icon name={iconName} size="medium" color={iconColor} />
                                <div className="fm-activities-marketing-campaign-interactions__description">
                                    {type === 'click' && url ? (
                                        <>
                                            <Text type="body">
                                                {getMarketingCampaignInteractionLabel('click')}
                                            </Text>
                                            <Link
                                                href={url}
                                                isTruncated
                                                target="_blank"
                                                className="fm-activities-marketing-campaign-interactions__link"
                                                variant="primary"
                                            >
                                                {displayUrl}
                                            </Link>
                                        </>
                                    ) : (
                                        <Text type="body">
                                            {getMarketingCampaignInteractionLabel(type)}
                                        </Text>
                                    )}
                                </div>
                            </div>
                            {dateText && (
                                <Text
                                    type="body"
                                    color={theme.colors.utility.textSecondary}
                                    className="fm-activities-marketing-campaign-interactions__date"
                                >
                                    {dateText}
                                </Text>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

DetailMarketingCampaignInteractions.displayName = 'DetailMarketingCampaignInteractions';

export default DetailMarketingCampaignInteractions;
